import type {
  OrderPlatformAddressDto,
  SupplierEmailAddressDto,
  SupplierOverviewDto,
} from '@/lib/api/v2/planning'

/**
 * SOLY-SUPPLIER-002 MS-01 — pure helpers of the suppliers page. The server is the authority on every
 * rule (address format, public domains, one owner per address); these only shape what is shown.
 */

/** The orders board, filtered on one supplier and on « En cours » (Sent + Confirmed). */
export function inProgressOrdersUrl(supplierId: string): string {
  return `/supplier-orders?supplier=${encodeURIComponent(supplierId)}&status=en-cours`
}

/**
 * What the « Adresses » column shows: the domain when the supplier has one (else its first address),
 * and how many more addresses the card holds.
 */
export function addressSummary(addresses: SupplierEmailAddressDto[]): { main: string | null; more: number } {
  if (addresses.length === 0) return { main: null, more: 0 }
  const main = addresses.find((a) => a.kind === 'Domain') ?? addresses[0]
  return { main: main.value, more: addresses.length - 1 }
}

/** A new supplier usually supplies its own brand: « Quooker » proposes the brand « Quooker ». */
export function ownBrandSuggestion(supplierName: string): string | null {
  const name = supplierName.trim().replace(/\s+/g, ' ')
  return name.length > 0 ? name : null
}

export type AddressOwner =
  | { kind: 'supplier'; supplier: SupplierOverviewDto }
  | { kind: 'platform'; platform: OrderPlatformAddressDto }

/**
 * True when two stored values claim the same mail: the same value, an address under a domain, or a
 * domain over an address — the server's « un propriétaire, y compris à travers un domaine ».
 */
export function overlaps(a: string, b: string): boolean {
  if (a === b) return true
  const domainOf = (v: string) => v.slice(v.indexOf('@'))
  if (a.startsWith('@') && !b.startsWith('@')) return domainOf(b) === a
  if (b.startsWith('@') && !a.startsWith('@')) return domainOf(a) === b
  return false
}

/**
 * Who already owns this value in the organization — the server refuses a second owner, and the page
 * turns the refusal into a way forward (« ajouter plutôt la marque à Agence Cuisines »). An exact
 * owner wins over one found through a domain.
 */
export function findAddressOwner(
  value: string,
  suppliers: SupplierOverviewDto[],
  platforms: OrderPlatformAddressDto[],
): AddressOwner | null {
  const normalized = value.trim().toLowerCase()
  if (!normalized) return null
  for (const match of [(v: string) => v === normalized, (v: string) => overlaps(v, normalized)]) {
    const supplier = suppliers.find((s) => s.emailAddresses.some((a) => match(a.value)))
    if (supplier) return { kind: 'supplier', supplier }
    const platform = platforms.find((p) => match(p.value))
    if (platform) return { kind: 'platform', platform }
  }
  return null
}

/** Case- and spacing-insensitive brand match, the same rule as the server (PurchaseBrand.SameBrand). */
export function sameBrand(a: string, b: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()
  return norm(a) === norm(b)
}

/** Brand rename is limited to case and spacing in V1 — checked before calling the server. */
export function isSpellingCorrection(current: string, next: string): boolean {
  return next.trim().length > 0 && sameBrand(current, next)
}

/** True when the brand is the supplier's own (« Quooker » at « Quooker Benelux »): shown first and marked. */
export function isOwnBrand(supplierName: string, brandName: string): boolean {
  const supplier = supplierName.trim().replace(/\s+/g, ' ').toLowerCase()
  const brand = brandName.trim().replace(/\s+/g, ' ').toLowerCase()
  return brand.length > 0 && (supplier === brand || supplier.startsWith(`${brand} `))
}
